import * as Dialog from "@radix-ui/react-dialog";
import { Command } from "cmdk";
import {
  useCallback,
  useId,
  useRef,
  useState,
  type ReactElement
} from "react";
import { KeybindV2 } from "./ui/keybind-v2.tsx";
import { useKeybindings, usePaletteShortcut } from "./keybinds/react.tsx";
import { useCommands, useAfterCloseCommand } from "./commands/react.tsx";
import type { SurfaceCommand } from "./commands/model.ts";
export type { SurfaceCommand } from "./commands/model.ts";

export interface GlobalCommandPaletteProps {
  /** Omit to run uncontrolled; only an uncontrolled palette binds ⌘K. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly extraCommands?: readonly SurfaceCommand[];
  /** Include commands published through the surface registry. */
  readonly includeRegistry?: boolean;
  readonly trigger?: ReactElement;
  readonly title?: string;
  readonly description?: string;
  readonly closeLabel?: string;
  readonly searchLabel?: string;
  readonly searchPlaceholder?: string;
  readonly listLabel?: string;
  readonly emptyLabel?: string;
  /** Groups render in this order; any remaining groups follow, in order seen. */
  readonly groupOrder?: readonly string[];
}

export function GlobalCommandPalette({
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  extraCommands,
  includeRegistry = false,
  trigger,
  title = "Command Palette",
  description = "Search actions across workbench surfaces",
  closeLabel = "Close command palette",
  searchLabel = "Search commands",
  searchPlaceholder = "Type a command or search…",
  listLabel = "Available commands",
  emptyLabel = "No matching command.",
  groupOrder
}: GlobalCommandPaletteProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const descriptionId = useId();
  const afterCloseRef = useRef<(() => void) | undefined>(undefined);

  const controlled = externalOpen !== undefined;
  const isOpen = externalOpen ?? internalOpen;
  const dispatchAfterClose = useAfterCloseCommand(isOpen);

  const setOpen = useCallback(
    (value: boolean) => {
      if (externalOnOpenChange) externalOnOpenChange(value);
      if (!controlled) setInternalOpen(value);
    },
    [controlled, externalOnOpenChange]
  );

  const registryCommands = useCommands();

  // Only the uncontrolled instance owns the global chord. A controlled palette
  // is driven by its own surface, and binding here would mean one press toggled
  // every mounted palette at once.
  const keybindings = useKeybindings();
  usePaletteShortcut(!controlled, () => setInternalOpen(current => !current));

  const allCommands = [
    ...(includeRegistry ? registryCommands : []),
    ...(extraCommands ?? [])
  ];

  const seen = new Set(allCommands.map((command) => command.group));
  const groups = [
    ...(groupOrder ?? []).filter((group) => seen.has(group)),
    ...[...seen].filter((group) => !(groupOrder ?? []).includes(group))
  ];

  function execute(command: SurfaceCommand): void {
    if (command.enabled === false) return;
    if (command.runAfterClose) {
      // Deferred until onCloseAutoFocus so the command runs once, after the
      // dialog's focus trap is released.
      afterCloseRef.current = command.run;
      setOpen(false);
      return;
    }
    command.run();
    setOpen(false);
  }

  const content = (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-[9998] bg-black/45" />
      <Dialog.Content
        aria-describedby={descriptionId}
        data-component="global-command-palette"
        onCloseAutoFocus={(event) => {
          const afterClose = afterCloseRef.current;
          afterCloseRef.current = undefined;
          if (!afterClose) return;
          // Radix would otherwise restore focus to the trigger and undo whatever
          // the deferred command focuses.
          event.preventDefault();
          dispatchAfterClose(afterClose);
        }}
        className="fixed left-1/2 top-[12vh] z-[9999] flex max-h-[76vh] w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-kumo-line bg-kumo-base shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-kumo-line px-4 py-3">
          <div>
            <Dialog.Title className="text-sm font-semibold text-kumo-default">
              {title}
            </Dialog.Title>
            <Dialog.Description id={descriptionId} className="mt-1 text-xs text-kumo-subtle">
              {description}
            </Dialog.Description>
          </div>
          <Dialog.Close
            type="button"
            aria-label={closeLabel}
            className="rounded border border-kumo-line px-2 py-1 text-xs text-kumo-subtle hover:text-kumo-default"
          >
            Esc
          </Dialog.Close>
        </div>
        <Command
          label={title}
          className="flex min-h-0 flex-1 flex-col bg-kumo-base text-kumo-default"
        >
          <Command.Input
            autoFocus
            aria-label={searchLabel}
            placeholder={searchPlaceholder}
            className="h-11 shrink-0 border-b border-kumo-line bg-transparent px-4 text-sm outline-none placeholder:text-kumo-placeholder"
          />
          <Command.List
            aria-label={listLabel}
            className="min-h-0 flex-1 overflow-y-auto p-2"
          >
            <Command.Empty className="p-6 text-center text-xs text-kumo-subtle">
              {emptyLabel}
            </Command.Empty>
            {groups.map((group) => (
              <Command.Group
                key={group}
                heading={group}
                className="mb-2 text-xs text-kumo-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide"
              >
                {allCommands
                  .filter((command) => command.group === group)
                  .map((command) => {
                    const binding = keybindings.find(row => row.commandId === (command.shortcutId ?? command.id));
                    const keys = binding?.userKeys ?? binding?.defaultKeys ?? [];
                    const disabled = command.enabled === false;
                    return (
                      <Command.Item
                        key={command.id}
                        value={`${command.label} ${command.keywords?.join(" ") ?? ""}`}
                        disabled={disabled}
                        onSelect={() => execute(command)}
                        className="flex cursor-default items-center gap-3 rounded px-2 py-2 text-kumo-default outline-none data-[disabled=true]:opacity-45 data-[selected=true]:bg-kumo-elevated"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-medium">{command.label}</span>
                          {disabled && command.unavailableReason ? (
                            <span className="mt-0.5 block text-[11px] text-kumo-subtle">
                              {command.unavailableReason}
                            </span>
                          ) : null}
                        </span>
                        {keys.length > 0 ? (
                          <KeybindV2 keys={keys} variant="ghost" />
                        ) : command.shortcut ? (
                          <kbd className="shrink-0 rounded border border-kumo-line bg-kumo-elevated px-1.5 py-0.5 font-mono text-[10px] text-kumo-subtle">
                            {command.shortcut}
                          </kbd>
                        ) : null}
                      </Command.Item>
                    );
                  })}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </Dialog.Content>
    </Dialog.Portal>
  );

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (nextOpen) afterCloseRef.current = undefined;
        setOpen(nextOpen);
      }}
    >
      {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
      {content}
    </Dialog.Root>
  );
}
